import {
  DataStateChangeEvent,
  FilterService,
  GridDataResult,
} from '@progress/kendo-angular-grid';
import {
  ColumnGroup,
  ColumnOptions,
  GridRowType,
  GridState,
  GridStateCompositeFilterDescriptor,
  LoaderService,
  MessageService,
  ValidationResultService,
} from '@shared';
import {
  CategoryService,
  GlobalEventsService,
  IChangeset,
} from '@shared-data';
import { RestaurantItemService } from '../../services';
import {
  CompositeFilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import { Injector } from '@angular/core';
import { ExportOption, ImportOptions } from '../../constants';

export abstract class ChangesetComponent {
  columns: ColumnOptions[] = [];
  columnGroups: ColumnGroup[] = [];

  entity!: IChangeset & GridRowType;

  changeset: GridDataResult = { data: [], total: 0 };

  cache: Map<string, IChangeset & GridRowType> = new Map<
    string,
    IChangeset & GridRowType
  >();

  restaurantId?: string;
  restaurantIds?: string[];

  category: string | null = null;

  type!: string;

  state!: GridState;

  _fontSize: number = 0.875;

  set fontSize(value: number) {
    this._fontSize = value;
    this.setDefaultState();
  }

  get fontSize(): number {
    return this._fontSize;
  }

  importUrls!: Map<ImportOptions, string>;

  initialSort!: SortDescriptor[];
  initialFilter!: CompositeFilterDescriptor;

  filterCache: Map<string, string[]> = new Map<string, string[]>();

  _productTypeId!: string;

  set productTypeId(v: string) {
    this._productTypeId = v;
    if (v) {
      this.importUrls = this.service.getImportUrls(v, this.type);
    }
  }
  get productTypeId(): string {
    return this._productTypeId;
  }

  protected globalEventsService: GlobalEventsService;

  constructor(
    protected validationErrorService: ValidationResultService,
    protected messageService: MessageService,
    protected service: RestaurantItemService,
    protected categoryService: CategoryService,
    protected loaderService: LoaderService,
    protected injector: Injector,
    protected entityType?: new (...args: any[]) => IChangeset & GridRowType,
  ) {
    this.globalEventsService = this.injector.get(GlobalEventsService);
    this.entity = new this.entityType!();
    this.setColumns();
  }

  setColumns() {
    this.columns = this.entity.getColumns();
    this.columnGroups = this.entity.getColumnGroups(this.columns);
    this.setInitialSort();
    this.setInitialFilter();
  }

  setInitialSort() {
    this.initialSort = [
      { field: 'categoryOrder', dir: 'asc' },
      { field: 'sortOrder', dir: 'asc' },
    ];
  }

  setInitialFilter() {
    this.initialFilter = {
      logic: 'and',
      filters: [
      ]
    }
  }

  setDefaultState(): void {
    if (
      !this.restaurantId &&
      (!this.restaurantIds || this.restaurantIds.length === 0)
    ) {
      return;
    }

    this.state = new GridState(
      GridState.getPageSizeByFontSize(this.fontSize),
      this.initialSort,
      this.restaurantId,
      this.productTypeId,
      this.restaurantIds,
    );

    this.state.filter = this.state.filter?.and(this.initialFilter);

    let newFilter = new GridStateCompositeFilterDescriptor();

    this.columns.forEach((column) => {
      const values = this.filterCache.get(column.field);
      values?.forEach((val) => {
        newFilter = newFilter?.or({
          field: column.field,
          operator: 'eq',
          value: val,
        });
      });
    });

    this.state.filter = this.state.filter?.and(newFilter);

    this.fetchChangesets(this.state);
  }

  fetchChangesets(event: GridState | DataStateChangeEvent) {
    const state = event as GridState;

    if (state.filter) {
      this.state.filter = state.filter;
    }
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = this.initialSort;
    }

    if (state.skip !== undefined) this.state.skip = state.skip;

    if (state.take !== undefined) this.state.take = state.take;

    if (this.state.filter)
      this.state.filter = new GridStateCompositeFilterDescriptor(
        GridState.removeFilterByFields(this.state.filter, 'categoryId'),
      );

    if (this.category != null) {
      this.state.filter = this.state.filter?.and({
        field: 'categoryId',
        operator: 'eq',
        value: this.category,
      });
    }

    this.state.restaurantId = this.restaurantId;
    this.state.productType = this.productTypeId;
    this.state.restaurantIds = this.restaurantIds;

    this.loaderService.show();

    this.service.getChangesets(this.type, this.state).subscribe({
      next: (gridDataResult) => {
        this.populateGrid(gridDataResult);
        this.restoreCachedChanges();
        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error('Failed to load changesets');

        var validationError = err?.error;

        if (validationError) {
          this.validationErrorService.show(validationError);
        }

        this.loaderService.hide();
      },
    });
  }

  // hook for populating grid data
  abstract populateGrid(gridDataResult: GridDataResult): void;

  restoreCachedChanges(): void {
    this.changeset.data.forEach((row) => {
      const cachedRow = this.cache.get(row.id);

      if (cachedRow) {
        const editableColumns = this.columns.filter(
          (column) => column.editable,
        );
        editableColumns.forEach((column) => {
          row[column.field as keyof IChangeset] =
            cachedRow[column.field as keyof IChangeset];
        });

        row.dirty = cachedRow.dirty;
      }
    });
  }

  cacheChanges() {
    this.changeset.data.forEach((row) => {
      const editableColumns = this.columns.filter((column) => column.editable);
      const isDirty = editableColumns.some((column) =>
        row.dirty.get(column.field),
      );
      if (isDirty) {
        this.cache.set(row.id, row);
      } else {
        this.cache.delete(row.id);
      }

      row.warnings.clear();
      row.errors.clear();
    });
  }

  saveChanges(applyToRestaurants?: string[]) {
    this.loaderService.show();
    const changes = Array.from(this.cache.values());
    this.service
      .saveChangesets(
        this.type,
        changes,
        this.productTypeId,
        applyToRestaurants,
      )
      .subscribe({
        next: (result) => {
          this.messageService.success('Changes saved successfully');
          if (result?.errors?.length) {
            this.validationErrorService.show(result);
          }
          this.cache.clear();

          this.clearDirtyRows();

          this.fetchChangesets(this.state);

          this.globalEventsService.updatePendingChanges();

          this.loaderService.hide();
        },
        error: (err) => {
          this.messageService.error(
            'Changes could not be saved due to validation errors',
          );
          const validationError = err?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }

          this.loaderService.hide();
        },
      });
  }

  clearDirtyRows() {
    this.changeset.data.forEach((row) => row.dirty.clear());
  }

  exportToExcel(exportOption: ExportOption = 'Current Type') {
    if (this.restaurantId) {
      this.loaderService.show();
      this.service
        .exportToExcel(
          this.type,
          this.restaurantId,
          this.productTypeId,
          exportOption,
        )
        .subscribe({
          next: (data: Blob) => {
            const blob = new Blob([data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${this.type.charAt(0).toUpperCase() + this.type.slice(1)} Export ${new Date().toLocaleString('en-US')} ${
              exportOption === 'All'
                ? exportOption
                : `${exportOption} ${this.productTypeId}`
            }.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.loaderService.hide();
          },
          error: (err) => {
            this.loaderService.hide();

            this.messageService.error('Failed to export changesets');

            var validationError = err?.error;
            if (validationError) {
              this.validationErrorService.show(validationError);
            }
          },
        });
    }
  }

  executeFilter(
    filterService: FilterService,
    column: string,
    values: string[],
  ) {
    filterService.filter({
      filters: values.map((value) => ({
        field: column,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  onCustomFilterChange(
    column: string,
    event: any,
    filterService: FilterService,
  ) {
    const values = event as string[];
    this.filterCache.set(column, values);

    this.executeFilter(filterService, column, values);
  }

  filterChanged(event: CompositeFilterDescriptor) {
    if (event.filters.length === 0) {
      this.filterCache.clear();
    }

    for (const field of this.filterCache.keys()) {
      if (!GridState.hasField(event, field)) {
        this.filterCache.delete(field);
      }
    }
  }
}
